/**
 * Chip-based input for text[] fields (hobbies, favourite_foods,
 * dietary, allergies). One reusable primitive across the profile
 * surface + future chat widget + browse filter.
 *
 * Pioneer alignment (ux-pioneers-wisdom.md):
 * - Norman: chips LOOK like chips (rounded background, visible ✕).
 *   Affordance is communicated by the shape, not by a tooltip.
 * - Nielsen #6 (recognition vs recall): suggestion chips show common
 *   values for the user to click rather than type from scratch.
 * - Wroblewski: inline validation (duplicate detection); top-aligned
 *   label; smart defaults via suggestions.
 * - Stanley: aria-label on the input announces current state ("Hobbies,
 *   currently 3: pottery, k-drama, climbing"); chips removable via
 *   keyboard (Backspace from input).
 *
 * Behaviour:
 * - Type and press Enter to add a chip
 * - Click ✕ on a chip to remove
 * - Backspace from empty input removes the last chip
 * - Click a suggestion chip to add it (one-click add)
 * - Duplicates are silently rejected (case-insensitive)
 * - Empty values rejected
 */

import { useState, useRef, KeyboardEvent } from "react";
import { X } from "lucide-react";

interface ChipInputProps {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** Optional ghost-chip suggestions shown below the input. Click to add. */
  suggestions?: string[];
  placeholder?: string;
  /** Microcopy under the input. Wisdom-doc: "Why this matters." */
  hint?: string;
  /** When duplicates would be added, brief inline warning text. */
  duplicateMessage?: string;
  /** Lowercased values not allowed to be added (e.g. you'd block "n/a"). */
  blockedValues?: string[];
}

function normalise(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

export function ChipInput({
  label,
  values,
  onChange,
  suggestions = [],
  placeholder = "Type and press Enter…",
  hint,
  duplicateMessage = "Already added",
  blockedValues = [],
}: ChipInputProps) {
  const [draft, setDraft] = useState("");
  const [warning, setWarning] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const lowercaseValues = new Set(values.map((v) => v.toLowerCase()));
  const lowercaseBlocked = new Set(blockedValues.map((v) => v.toLowerCase()));

  const tryAdd = (raw: string) => {
    const clean = normalise(raw);
    if (!clean) return;
    const lower = clean.toLowerCase();
    if (lowercaseBlocked.has(lower)) {
      setWarning(`"${clean}" can't be added here.`);
      return;
    }
    if (lowercaseValues.has(lower)) {
      setWarning(duplicateMessage);
      return;
    }
    setWarning(null);
    onChange([...values, clean]);
    setDraft("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      tryAdd(draft);
    } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
      // Remove last chip on Backspace from empty input (standard chip pattern)
      e.preventDefault();
      onChange(values.slice(0, -1));
    } else if (e.key === "," && draft.trim()) {
      // Comma also commits, common chip-input behaviour
      e.preventDefault();
      tryAdd(draft);
    }
  };

  const handleRemove = (idx: number) => {
    onChange([...values.slice(0, idx), ...values.slice(idx + 1)]);
    inputRef.current?.focus();
  };

  // Suggestions filter, don't show ones the user already has
  const visibleSuggestions = suggestions.filter(
    (s) => !lowercaseValues.has(s.toLowerCase()),
  );

  return (
    <div className="space-y-2">
      <label
        className="block text-sm font-semibold text-navy"
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        {label}
      </label>

      <div
        className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 focus-within:border-coral"
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        {values.map((v, idx) => (
          <span
            key={`${v}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full bg-butter px-3 py-1 text-sm font-medium text-navy"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {v}
            <button
              type="button"
              aria-label={`Remove ${v}`}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(idx);
              }}
              className="rounded-full p-0.5 hover:bg-navy/10 focus:outline-none focus:ring-2 focus:ring-coral"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (warning) setWarning(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Commit pending draft on blur so users don't lose typed
            // values when they tab away
            if (draft.trim()) tryAdd(draft);
          }}
          placeholder={values.length === 0 ? placeholder : ""}
          aria-label={`${label}, currently ${values.length}: ${values.join(", ") || "none"}`}
          /* MS204: text-base on mobile to dodge iOS Safari auto-
             zoom-on-focus (triggered when computed font-size <16px).
             md:text-sm preserves the chip-row density on desk. */
          className="flex-1 min-w-[120px] bg-transparent text-base md:text-sm text-navy outline-none placeholder:text-charcoal/40"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        />
      </div>

      {warning && (
        <p className="text-xs text-coral" style={{ fontFamily: "DM Sans, sans-serif" }}>
          {warning}
        </p>
      )}

      {visibleSuggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="text-xs text-charcoal/55"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            Try:
          </span>
          {visibleSuggestions.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => tryAdd(s)}
              className="rounded-full border border-navy/20 px-2.5 py-0.5 text-xs text-navy/80 hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {hint && (
        <p
          className="text-xs text-charcoal/55"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}
